import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import api from '../utils/api';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.post('/contact', formData);
      
      if (response.data.success) {
        setSubmitted(true);
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
        setTimeout(() => {
          setSubmitted(false);
        }, 5000);
      }
    } catch (err) {
      console.error('Contact form error:', err);
      setError(
        err.response?.data?.error || 
        'Failed to send message. Please try again later or contact us directly at info@slnbooks.com'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="App">
      <Header />
      <div className="page contact-page">
        {/* Animated Background Elements */}
        <div className="animated-bg">
          <div className="floating-shape shape-1"></div>
          <div className="floating-shape shape-2"></div>
          <div className="floating-shape shape-3"></div>
          <div className="floating-shape shape-4"></div>
          <div className="floating-shape shape-5"></div>
          <div className="gradient-orb orb-1"></div>
          <div className="gradient-orb orb-2"></div>
          <div className="gradient-orb orb-3"></div>
        </div>

        {/* Hero Section */}
        <div className="contact-hero">
          <div className="contact-hero-content">
            <div className="hero-badge">Let's Connect</div>
            <h1 className="contact-hero-title">
              <span className="hero-title-main">
                <span className="title-word">Get</span>
                <span className="title-word">In</span>
                <span className="title-word highlight">Touch</span>
              </span>
            </h1>
            <p className="contact-hero-description">
              We're here to help! Whether you have questions, need support, or just want to say hello, 
              our team is ready to assist you.
            </p>
            <div className="hero-stats">
              <div className="stat-item">
                <div className="stat-icon">⚡</div>
                <div className="stat-number">24/7</div>
                <div className="stat-label">Support</div>
              </div>
              <div className="stat-item">
                <div className="stat-icon">🚀</div>
                <div className="stat-number">&lt;24hrs</div>
                <div className="stat-label">Response</div>
              </div>
              <div className="stat-item">
                <div className="stat-icon">✨</div>
                <div className="stat-number">100%</div>
                <div className="stat-label">Satisfaction</div>
              </div>
            </div>
          </div>
        </div>

        <div className="container">
          <div className="contact-content">
            {/* Left Side - Contact Info with 3D Cards */}
            <div className="contact-info-section">
              <div className="section-header">
                <span className="section-badge">Ways to Reach Us</span>
                <h2 className="section-title">Connect With Us</h2>
              </div>

              <div className="contact-info-grid">
                <div className="contact-info-card card-3d">
                  <div className="card-inner">
                    <div className="card-front">
                      <div className="contact-icon-wrapper">
                        <div className="contact-icon">
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                            <circle cx="12" cy="10" r="3"></circle>
                          </svg>
                        </div>
                      </div>
                      <h3>Visit Us</h3>
                      <div className="card-front-content">
                        <div className="business-name-front">SLN BOOK WORLD</div>
                        <p className="address-text-front">
                          Ground Floor, 4 & 5, 6th Main Road,<br />
                          Dattatreya Nagar, Banashankari Stage 3,<br />
                          Bengaluru – 560085
                        </p>
                      </div>
                    </div>
                    <div className="card-back">
                      <h3>📍 Our Location</h3>
                      <div className="business-name">SLN BOOK WORLD</div>
                      <p className="address-text">
                        Ground Floor, 4 & 5, 6th Main Road,<br />
                        Dattatreya Nagar, Banashankari Stage 3,<br />
                        Bengaluru – 560085
                      </p>
                      <a href="https://maps.google.com/?q=Ground+Floor,+4+%26+5,+6th+Main+Road,+Dattatreya+Nagar,+Banashankari+Stage+3,+Bengaluru,+560085" 
                         target="_blank" 
                         rel="noopener noreferrer" 
                         className="contact-link">
                        <span>View on Map</span>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                          <polyline points="12 5 19 12 12 19"></polyline>
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>

                <div className="contact-info-card card-3d">
                  <div className="card-inner">
                    <div className="card-front">
                      <div className="contact-icon-wrapper">
                        <div className="contact-icon">
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                          </svg>
                        </div>
                      </div>
                      <h3>Call Us</h3>
                      <div className="card-front-content">
                        <div className="phone-list-front">
                          <a href="tel:+919916832476" className="contact-phone-front">+91-9916832476</a>
                          <a href="tel:+919663622125" className="contact-phone-front">+91-9663622125</a>
                        </div>
                        <p className="contact-note-front">Available during business hours</p>
                      </div>
                    </div>
                    <div className="card-back">
                      <h3>📞 Phone Numbers</h3>
                      <div className="phone-list">
                        <a href="tel:+919916832476" className="contact-phone">
                          <span>+91-9916832476</span>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                          </svg>
                        </a>
                        <a href="tel:+919663622125" className="contact-phone">
                          <span>+91-9663622125</span>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                          </svg>
                        </a>
                      </div>
                      <p className="contact-note">Available during business hours</p>
                    </div>
                  </div>
                </div>

                <div className="contact-info-card card-3d">
                  <div className="card-inner">
                    <div className="card-front">
                      <div className="contact-icon-wrapper">
                        <div className="contact-icon">
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                            <polyline points="22,6 12,13 2,6"></polyline>
                          </svg>
                        </div>
                      </div>
                      <h3>Email Us</h3>
                      <div className="card-front-content">
                        <div className="email-list-front">
                          <a href="mailto:info@slnbooks.com" className="contact-email-front">info@slnbooks.com</a>
                          <a href="mailto:support@slnbooks.com" className="contact-email-front">support@slnbooks.com</a>
                        </div>
                        <p className="contact-note-front">We respond within 24 hours</p>
                      </div>
                    </div>
                    <div className="card-back">
                      <h3>✉️ Email Addresses</h3>
                      <div className="email-list">
                        <a href="mailto:info@slnbooks.com" className="contact-email">
                          <span>info@slnbooks.com</span>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                            <polyline points="22,6 12,13 2,6"></polyline>
                          </svg>
                        </a>
                        <a href="mailto:support@slnbooks.com" className="contact-email">
                          <span>support@slnbooks.com</span>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                            <polyline points="22,6 12,13 2,6"></polyline>
                          </svg>
                        </a>
                      </div>
                      <p className="contact-note">We respond within 24 hours</p>
                    </div>
                  </div>
                </div>

                <div className="contact-info-card card-3d">
                  <div className="card-inner">
                    <div className="card-front">
                      <div className="contact-icon-wrapper">
                        <div className="contact-icon">
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"></circle>
                            <polyline points="12 6 12 12 16 14"></polyline>
                          </svg>
                        </div>
                      </div>
                      <h3>Business Hours</h3>
                      <div className="card-front-content">
                        <div className="hours-list-front">
                          <div className="hours-item-front">
                            <span className="hours-day-front">Mon - Fri</span>
                            <span className="hours-time-front">9:00 AM - 6:00 PM</span>
                          </div>
                          <div className="hours-item-front">
                            <span className="hours-day-front">Saturday</span>
                            <span className="hours-time-front">10:00 AM - 4:00 PM</span>
                          </div>
                          <div className="hours-item-front">
                            <span className="hours-day-front">Sunday</span>
                            <span className="hours-time-front closed-front">Closed</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="card-back">
                      <h3>🕐 Operating Hours</h3>
                      <div className="hours-list">
                        <div className="hours-item">
                          <span className="hours-day">Monday - Friday</span>
                          <span className="hours-time">9:00 AM - 6:00 PM</span>
                        </div>
                        <div className="hours-item">
                          <span className="hours-day">Saturday</span>
                          <span className="hours-time">10:00 AM - 4:00 PM</span>
                        </div>
                        <div className="hours-item">
                          <span className="hours-day">Sunday</span>
                          <span className="hours-time closed">Closed</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="gst-info-card">
                <div className="gst-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                </div>
                <div className="gst-content">
                  <div className="gst-business-name">SLN BOOK WORLD</div>
                  <p className="gst-number"><strong>GST NO:</strong> 29BZYPN1098P2ZY</p>
                </div>
              </div>
            </div>

            {/* Right Side - Creative Form */}
            <div className="contact-form-section">
              <div className="form-header">
                <span className="form-badge">Drop Us a Line</span>
                <h2 className="form-title">Send Message</h2>
                <p className="form-subtitle">We'd love to hear from you</p>
              </div>
              
              {submitted && (
                <div className="success-message">
                  <div className="success-icon-wrapper">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                  </div>
                  <div className="success-content">
                    <strong>Message Sent Successfully!</strong>
                    <p>Thank you for contacting us. We'll get back to you within 24 hours.</p>
                  </div>
                </div>
              )}

              {error && (
                <div className="error-message" style={{
                  padding: '15px',
                  marginBottom: '20px',
                  backgroundColor: '#fee',
                  border: '1px solid #fcc',
                  borderRadius: '8px',
                  color: '#c33'
                }}>
                  <strong>Error:</strong> {error}
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group floating-label">
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder=" "
                    className="form-input"
                  />
                  <label className="form-label">Full Name *</label>
                  <div className="input-underline"></div>
                </div>

                <div className="form-group floating-label">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder=" "
                    className="form-input"
                  />
                  <label className="form-label">Email Address *</label>
                  <div className="input-underline"></div>
                </div>

                <div className="form-group floating-label">
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder=" "
                    className="form-input"
                  />
                  <label className="form-label">Phone Number</label>
                  <div className="input-underline"></div>
                </div>

                <div className="form-group floating-label">
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    placeholder=" "
                    className="form-input"
                  />
                  <label className="form-label">Subject *</label>
                  <div className="input-underline"></div>
                </div>

                <div className="form-group floating-label">
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    placeholder=" "
                    className="form-input form-textarea"
                  />
                  <label className="form-label">Message *</label>
                  <div className="input-underline"></div>
                </div>

                <button type="submit" className="btn-submit" disabled={loading}>
                  <span>{loading ? 'Sending...' : 'Send Message'}</span>
                  {!loading && (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="22" y1="2" x2="11" y2="13"></line>
                      <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                    </svg>
                  )}
                </button>
              </form>
            </div>
          </div>

          <div className="map-section">
            <div className="map-header">
              <h2>Find Us</h2>
            </div>
            <div className="map-container">
              <iframe
                src="https://www.google.com/maps?q=Ground+Floor,+4+%26+5,+6th+Main+Road,+Dattatreya+Nagar,+Banashankari+Stage+3,+Bengaluru,+560085,+Karnataka,+India&output=embed"
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="SLN Book World Location"
              ></iframe>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Contact;

